import { ArticleFromApi, CategoryFromApi } from 'products/types'
import { TimeSlotGroup } from 'timeSlots/timeSlotsService'
import {
    applicableGroups,
    categoryGroupContext,
    CategoryTimeSlotInfo,
    categoryTimeSlotInfo,
    contextKey,
    GroupContext,
} from 'timeSlots/domain/applicableGroups'
import { FORM_SECTION } from 'capacity/copy'
import { articleCategoryIds, categoryName, isInCategory, usesCapacity } from './capacityRows'

export interface GroupBlock {
    label: string
    groups: TimeSlotGroup[]
}

export function productGroupBlocks(
    product: { id: string; categoryIds: string[] },
    groups: TimeSlotGroup[],
    categories: CategoryFromApi[],
): { blocks: GroupBlock[]; dependsOnCategory: boolean } {
    const info = categoryTimeSlotInfo(categories)
    const resolved = applicableGroups(product, groups, info)
    const blocks: GroupBlock[] = []
    const own = new Set(product.categoryIds)
    const push = (label: string, context: GroupContext) => {
        const existing = blocks.find((block) => block.label === label)
        if (existing) {
            context.groups.forEach((group) => {
                if (!existing.groups.some((entry) => entry.id === group.id)) {
                    existing.groups.push(group)
                }
            })
        } else {
            blocks.push({ label, groups: [...context.groups] })
        }
    }
    for (const context of resolved.contexts) {
        if (context.groups.length === 0) {
            continue
        }
        if (context.scope === 'product') {
            push(FORM_SECTION.setOnProduct, context)
        } else if (context.scope === 'category') {
            const name = context.categoryName || categoryName(categories, context.categoryId)
            push(
                own.has(context.categoryId ?? '')
                    ? FORM_SECTION.fromCategory(name)
                    : FORM_SECTION.fromParentCategory(name),
                context,
            )
        } else {
            push(FORM_SECTION.defaultForAll, context)
        }
    }
    const distinctContexts = new Set(resolved.contexts.map(contextKey))
    return { blocks, dependsOnCategory: distinctContexts.size > 1 }
}

export function categoryGroupBlocks(
    categoryId: string,
    groups: TimeSlotGroup[],
    categories: CategoryFromApi[],
): GroupBlock[] {
    const info: CategoryTimeSlotInfo[] = categoryTimeSlotInfo(categories).map((category) =>
        category.id === categoryId ? { ...category, timeSlotsEnabled: true } : category,
    )
    const context = categoryGroupContext(categoryId, groups, info)
    if (context.groups.length === 0) {
        return []
    }
    if (context.scope === 'category') {
        const label =
            context.categoryId === categoryId
                ? FORM_SECTION.setOnCategory
                : FORM_SECTION.fromParentCategory(context.categoryName || categoryName(categories, context.categoryId))
        return [{ label, groups: context.groups }]
    }
    return [{ label: FORM_SECTION.defaultForAll, groups: context.groups }]
}

export function productsLosingTimeSlots(
    categoryId: string,
    articles: ArticleFromApi[],
    groups: TimeSlotGroup[],
    categories: CategoryFromApi[],
): number {
    const info = categoryTimeSlotInfo(categories)
    const on = info.map((c) => (c.id === categoryId ? { ...c, timeSlotsEnabled: true } : c))
    const off = info.map((c) => (c.id === categoryId ? { ...c, timeSlotsEnabled: false } : c))
    return articles.filter((article) => {
        if (!usesCapacity(article) || !isInCategory(article, categoryId)) {
            return false
        }
        const product = { id: article.id, categoryIds: articleCategoryIds(article) }
        return (
            applicableGroups(product, groups, on).window === 'slot' &&
            applicableGroups(product, groups, off).window === 'day'
        )
    }).length
}

export const categoriesAllSwitchedOff = (categoryIds: string[], categories: CategoryFromApi[]): boolean =>
    categoryIds.length > 0 &&
    categoryIds.every((id) => categories.find((category) => category.id === id)?.timeSlotsEnabled === false)
